import { Fragment, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { ROUTES } from "../lib/routes";
import { initials } from "../lib/staff";
import { useMergedFaculty } from "../lib/mergedFaculty";
import type { MergedStaffMember } from "../lib/mergedFaculty";

type Category = "ALL" | "TEACHING" | "NON_TEACHING";

const CATEGORIES: Array<{ key: Category; label: string }> = [
  { key: "ALL", label: "Everyone" },
  { key: "TEACHING", label: "Teaching" },
  { key: "NON_TEACHING", label: "Non-teaching" },
];

/**
 * Teaching staff split into exactly two segments — Heads of Department, then
 * everyone else — rather than one segment per designation. Each segment keeps
 * the merged list's own order: webadmin's display order for anyone published,
 * then everyone else in staff.ts's order (teaching-first, by designation
 * seniority), so rank is still visible without a header per rank.
 */
function splitTeaching(list: MergedStaffMember[]): Array<{ title: string; people: MergedStaffMember[] }> {
  const hod = list.filter((s) => s.designation.includes("Head"));
  const faculty = list.filter((s) => !s.designation.includes("Head"));
  return [
    { title: "Head of Department", people: hod },
    { title: "Faculty", people: faculty },
  ].filter((g) => g.people.length > 0);
}

function StaffCard({ s }: { s: MergedStaffMember }) {
  return (
    <Link to={`${ROUTES.faculty}/${s.slug}`} className="faculty-card">
      <div className="faculty-avatar">
        <Figure
          media={s.photo}
          fallbackLabel={initials(s.name)}
          aspect="1/1"
          className="faculty-photo"
          sizes="56px"
        />
      </div>
      <div className="faculty-info">
        <div className="faculty-name">{s.name}</div>
        <div className="faculty-role">{s.designation}</div>
        {s.deptName && <div className="faculty-qual">{s.deptName}</div>}
      </div>
    </Link>
  );
}

export default function Faculty() {
  const [params] = useSearchParams();
  const { list: STAFF, depts: STAFF_DEPTS } = useMergedFaculty();
  const initialDept = (() => {
    const p = (params.get("dept") || "").toUpperCase();
    return STAFF_DEPTS.some((d) => d.code.toUpperCase() === p) ? p : "";
  })();

  const [dept, setDept] = useState(initialDept);
  const [category, setCategory] = useState<Category>("ALL");

  const filtered = useMemo(() => {
    return STAFF.filter((s) => {
      if (dept && s.deptCode.toUpperCase() !== dept) return false;
      if (category === "TEACHING" && !s.teaching) return false;
      if (category === "NON_TEACHING" && s.teaching) return false;
      return true;
    });
  }, [STAFF, dept, category]);

  const sections = useMemo(() => {
    const teaching = filtered.filter((s) => s.teaching);
    const nonTeaching = filtered.filter((s) => !s.teaching);
    return [
      { key: "teaching", title: "Teaching staff", list: teaching },
      { key: "non-teaching", title: "Non-teaching staff", list: nonTeaching },
    ].filter((sec) => sec.list.length > 0);
  }, [filtered]);

  const clearAll = () => { setDept(""); setCategory("ALL"); };
  const hasFilters = dept !== "" || category !== "ALL";

  return (
    <>
      {/* Quality over headcount, in the institute's own words: the bietdvg.edu
          home page's HIGHLIGHTS strip reads "Highly qualified & dedicated
          teaching faculty and supporting staff". */}
      <PageHero
        variant="hero-editorial hero-compact"
        fullWidthTitle
        eyebrow="People"
        title="Professors, instructors, and the hands that keep the campus running."
        lead="Highly qualified and dedicated teaching faculty and supporting staff, as the institute describes its own — listed by seniority within each group. Filter by department or category."
        crumbs={[{ label: "About", to: ROUTES.about }, { label: "Faculty & staff" }]}
        image={null}
      />

      <section className="section">
        <div className="container">
          <div className="faculty-shell">
            <aside className="faculty-rail" aria-label="Filter staff">
              <div className="faculty-rail-section">
                <div className="faculty-rail-label">Category</div>
                <ul className="faculty-rail-roles">
                  {CATEGORIES.map((c) => (
                    <li key={c.key}>
                      <button
                        type="button"
                        className={"faculty-role-btn" + (category === c.key ? " active" : "")}
                        onClick={() => setCategory(c.key)}
                      >
                        {c.label}
                        <span className="faculty-rail-count">
                          {c.key === "ALL"
                            ? STAFF.length
                            : STAFF.filter((s) => (c.key === "TEACHING" ? s.teaching : !s.teaching)).length}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="faculty-rail-section">
                <div className="faculty-rail-label">Department</div>
                <ul className="faculty-rail-list">
                  {STAFF_DEPTS.map((d, i) => (
                    <Fragment key={d.code}>
                      {/* Academic programmes run first; the support units that follow
                          get a divider so the break is visible. */}
                      {d.group === "other" && STAFF_DEPTS[i - 1]?.group === "program" && (
                        <li className="faculty-rail-divider" aria-hidden="true">Other</li>
                      )}
                      <li>
                      <button
                        type="button"
                        className={"faculty-rail-btn" + (dept === d.code.toUpperCase() ? " active" : "")}
                        onClick={() => setDept(dept === d.code.toUpperCase() ? "" : d.code.toUpperCase())}
                      >
                        <span>
                          <span className="faculty-rail-code">{d.code}</span>
                          <span className="faculty-rail-name">{d.name}</span>
                        </span>
                        <span className="faculty-rail-count">{d.count}</span>
                      </button>
                      </li>
                    </Fragment>
                  ))}
                </ul>
              </div>

              <div className="faculty-rail-foot">
                <span className="faculty-rail-tally">
                  Showing <b>{filtered.length}</b> of {STAFF.length}
                </span>
                {hasFilters && (
                  <button type="button" className="faculty-rail-clear" onClick={clearAll}>
                    Clear filters
                  </button>
                )}
              </div>
            </aside>

            <div className="faculty-content">
              {sections.length === 0 && (
                <div className="faculty-empty">
                  No staff match your filters. Try clearing them.
                </div>
              )}

              {sections.map((sec) => (
                <div key={sec.key}>
                  <div className="faculty-dept-head">
                    <h3>{sec.title}</h3>
                    <span className="count">{sec.list.length} people</span>
                  </div>

                  {sec.key === "teaching" ? (
                    splitTeaching(sec.list).map((g) => (
                      <div key={g.title} className="faculty-dept-group">
                        <div className="faculty-desig-head">
                          {g.title}
                          <span>{g.people.length}</span>
                        </div>
                        <div className="faculty-grid">
                          {g.people.map((s) => <StaffCard key={s.slug} s={s} />)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="faculty-grid">
                      {sec.list.map((s) => <StaffCard key={s.slug} s={s} />)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
